/**
 * Toasts, for something that already happened.
 *
 * Anything that needs a decision is a `Sheet`, not a toast — a message that has to
 * be caught in four seconds is not a decision point.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

export type ToastTone = 'info' | 'success' | 'warn' | 'danger'

interface Toast {
  id: number
  tone: ToastTone
  message: string
  action?: { label: string; onClick: () => void }
}

interface ToastValue {
  show: (message: string, options?: { tone?: ToastTone; action?: Toast['action'] }) => void
}

const ToastContext = createContext<ToastValue | null>(null)

/* `success` is deliberately not the marking green. A saved draft is not a mark
   awarded, and spending the signature colour on a toast is what stops it
   meaning anything on the one screen it exists for. The ✓ glyph below carries
   the semantics, which is the dual-encoding the guideline asks for anyway. */
const TONE_COLOUR: Record<ToastTone, string> = {
  info: 'var(--info)',
  success: 'var(--info)',
  warn: 'var(--warn)',
  danger: 'var(--danger)',
}

const GLYPH: Record<ToastTone, string> = {
  info: 'ℹ',
  success: '✓',
  warn: '⚠',
  danger: '✕',
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const nextId = useRef(0)

  const show = useCallback<ToastValue['show']>((message, options) => {
    const id = (nextId.current += 1)
    const toast: Toast = {
      id,
      message,
      tone: options?.tone ?? 'info',
      ...(options?.action ? { action: options.action } : {}),
    }
    setToasts((current) => [...current, toast])

    // A toast with an action stays longer: four seconds is not enough to read it,
    // decide, and reach the button.
    const ttl = toast.action ? 8000 : 4000
    setTimeout(() => setToasts((current) => current.filter((t) => t.id !== id)), ttl)
  }, [])

  const value = useMemo(() => ({ show }), [show])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="mx-toasts" aria-live="polite" aria-atomic="false">
        {toasts.map((toast) => (
          <div key={toast.id} className="mx-toast mx-glass mx-glass-overlay">
            <span style={{ color: TONE_COLOUR[toast.tone] }} aria-hidden="true">
              {GLYPH[toast.tone]}
            </span>
            <span style={{ minWidth: 0 }}>{toast.message}</span>
            {toast.action && (
              <button
                type="button"
                onClick={() => {
                  toast.action?.onClick()
                  setToasts((current) => current.filter((t) => t.id !== toast.id))
                }}
                style={{
                  marginLeft: 'auto',
                  background: 'none',
                  border: 0,
                  color: 'var(--accent)',
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {toast.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastValue {
  const value = useContext(ToastContext)
  if (!value) throw new Error('useToast must be used inside a ToastProvider')
  return value
}
